import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  TableBorderRadiusTopLeftRight,
  StyledTableCell,
  pageSize,
  TablePaginationStyle,
  paginationLabel,
  NoDataContainer,
} from "../Common/styles/table";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import DownloadIcon from "@mui/icons-material/Download";
import { EditOutlined, Visibility } from "@mui/icons-material";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { StyledHeader } from "../Common/styles/header";

const statusMap: any = {
  in_progress: { label: "Inprogress", color: "#F49D4C" },
  signed: { label: "Signed", color: "#70AE71" },
  not_started: { label: "Not Started", color: "#3F83F8" },
  provider_signed: { label: "Completed", color: "#70AE71" },
  verified: { label: "Verified", color: "#3F83F8" },
};
const ConsentFormsList = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState(0);
  const [page, setPage] = useState<number>(
    parseInt(sessionStorage.getItem("consent-page") || "0") ?? 0
  );

  const [loading, setLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const studyId = localStorage.getItem("study-id");
        let url = `/participant/study/${studyId}/econsent?page=${
          page + 1
        }&size=${pageSize}`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.rows?.map((item: any, index: number) => ({
          id: item?.consentVersion?.id,
          name: item?.consentVersion?.name,
          version: item?.consentVersion?.version,
          consentId: item?.consentVersion?.consentId,
          consentDate: item?.signedOn
            ? DateTime.fromISO(item?.signedOn).toFormat("MMM dd, yyyy")
            : "-",
          status: item?.status,
          sentOn: item?.sentOn
            ? DateTime.fromISO(item?.sentOn).toFormat("MMM dd, yyyy")
            : "-",
        }));

        setData(newData || []);
        setDataCount(data?.count || 0);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [page]);

  const handleDownload = async (versionId: string, consentId: string) => {
    try {
      setDownloadingRowId(versionId);
      const studyId = localStorage.getItem("study-id");
      let res = await http.post(
        `/participant/study/${studyId}/econsent/${consentId}/versions/${versionId}/download`
      );
      const downloadLink = res?.data?.data?.downloadUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
    } catch (err) {
      setDownloadingRowId("");
      errorToastMessage(err as Error);
    }
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const handleSignConsent = (consentId: string, versionId: string) => {
    const studyId = localStorage.getItem("study-id");
    navigate(`/sign/${consentId}?versionId=${versionId}&studyId=${studyId}`);
  };

  return (
    <>
      <StyledHeader>
        <Typography fontSize={20} fontWeight="medium" color="text.primary">
          Econsent
        </Typography>
      </StyledHeader>
      <Box sx={{ p: 2.5, overflow: "auto", height: "calc(100% - 80px)" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Consent Name</StyledTableCell>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>Consent Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            {!loading && data?.length > 0 && (
              <>
                <TableBody>
                  {data?.map((item: any) => (
                    <TableRow key={item?.id}>
                      <StyledTableCell>{item?.name}</StyledTableCell>
                      <StyledTableCell>{item?.version}</StyledTableCell>
                      <StyledTableCell>{item?.consentDate}</StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="subtitle1"
                          fontWeight="regular"
                          sx={{
                            color: statusMap[item?.status]?.color,
                          }}
                        >
                          {statusMap[item?.status]?.label}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {downloadingRowId === item?.id ? (
                          <CircularProgress size={18} />
                        ) : item?.status === "signed" ||
                          item?.status === "provider_signed" ||
                          item?.status === "verified" ? (
                          <>
                            <IconButton
                              onClick={() =>
                                handleSignConsent(item?.consentId, item?.id)
                              }
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleDownload(item?.id, item?.consentId)
                              }
                            >
                              <DownloadIcon />
                            </IconButton>
                          </>
                        ) : (
                          <Box sx={{ width: "80px", textAlign: "center" }}>
                            <IconButton
                              onClick={() =>
                                handleSignConsent(item?.consentId, item?.id)
                              }
                            >
                              <EditOutlined />
                            </IconButton>
                          </Box>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {dataCount > pageSize && (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        sx={TablePaginationStyle}
                        count={dataCount}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[pageSize]}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={paginationLabel}
                      />
                    </TableRow>
                  </TableFooter>
                )}
              </>
            )}
          </Table>
        </Box>
        {!loading && data.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </Box>
    </>
  );
};

export default ConsentFormsList;
